import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon.js';

export function Badge({ tone = 'muted', children }: {
  tone?: 'ok' | 'warn' | 'danger' | 'info' | 'muted';
  children: ReactNode;
}) {
  return <span className={`arc-badge arc-badge-${tone}`}>{children}</span>;
}

/**
 * Status is never carried by colour alone — the dot plus the text label do the
 * work, so it survives greyscale and colour blindness (doc 07 §5).
 */
export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, 'ok' | 'warn' | 'danger' | 'info' | 'muted'> = {
    active: 'ok', registered: 'ok', ready: 'ok', up: 'ok', published: 'ok',
    provisioning: 'info', pending: 'info', draining: 'warn', unknown: 'muted',
    suspended: 'warn', disabled: 'muted', closed: 'muted', draft: 'warn',
    failed: 'danger', unregistered: 'danger', down: 'danger', degraded: 'warn',
  };
  return (
    <Badge tone={map[status] ?? 'muted'}>
      <span className="arc-dot" aria-hidden="true" />
      {status}
    </Badge>
  );
}

export function Empty({ icon = 'search', title, hint, action }: {
  icon?: IconName; title: string; hint?: string; action?: ReactNode;
}) {
  return (
    <div className="arc-empty">
      <div className="arc-empty-icon"><Icon name={icon} size={17} /></div>
      <div className="arc-empty-title">{title}</div>
      {hint && <div style={{ fontSize: 12.5, maxWidth: 420, margin: '0 auto 12px' }}>{hint}</div>}
      {action}
    </div>
  );
}

export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <tbody>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }).map((__, c) => (
            <td key={c}>
              <div className="arc-skeleton" style={{ height: 11, width: c === 0 ? '58%' : '76%' }} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

export function Stat({ label, value, sub, tone }: {
  label: string; value: ReactNode; sub?: ReactNode | undefined;
  tone?: 'ok' | 'warn' | 'danger' | undefined;
}) {
  return (
    <div className="arc-card arc-stat">
      <div className="arc-stat-label">{label}</div>
      <div className="arc-stat-value" style={tone ? { color: `var(--arc-${tone})` } : undefined}>
        {value}
      </div>
      {sub !== undefined && <div className="arc-stat-sub">{sub}</div>}
    </div>
  );
}

export function ErrorBanner({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : 'Something went wrong';
  const requestId =
    typeof error === 'object' && error !== null && 'problem' in error
      ? (error as { problem?: { request_id?: string } }).problem?.request_id
      : undefined;
  return (
    <div className="arc-banner arc-banner-danger" style={{ marginBottom: 14 }} role="alert">
      <Icon name="warning" size={14} />
      <div>
        <div style={{ fontWeight: 600 }}>{message}</div>
        {/* Quoting this to support resolves straight to a trace (doc 05 §3). */}
        {requestId && (
          <div className="arc-mono" style={{ opacity: .75, marginTop: 2 }}>{requestId}</div>
        )}
      </div>
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder, label }: {
  value: string; onChange: (v: string) => void; placeholder?: string; label: string;
}) {
  return (
    <div className="arc-search">
      <Icon name="search" size={13} />
      <input
        className="arc-input" value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} aria-label={label}
      />
    </div>
  );
}
